/**
 * Neutral form-state surface backed by React Hook Form.
 *
 * `FormContext` is the only shape the form system exposes to field
 * callbacks, `visible` predicates, custom renderers, and the `useForm`
 * `ref`. Nothing here is named after the underlying library so consumers
 * stay decoupled from it.
 */
import { createContext, useContext, useEffect, useSyncExternalStore } from 'react'
import {
  useController,
  useFormContext as useReactHookFormContext,
  useWatch,
  type UseFormReturn,
  type FieldErrors,
} from 'react-hook-form'
import type { FormContext } from './generated-types'

type UploadFileFn = (args: {
  file: File
  use_case: string
  /** The field the file is for (see FormPropsConfig.uploadFile). */
  fieldName?: string
}) => Promise<{ uuid?: string; UUID?: string; file_name?: string; fileName?: string } & Record<string, unknown>>

const UploadContext = createContext<UploadFileFn | null>(null)
export const UploadProvider = UploadContext.Provider
export function useUpload(): UploadFileFn | null {
  return useContext(UploadContext)
}

/**
 * Which fields still have a file on its way up (picked, compressing or
 * uploading). The form holds a submit until none do, so a late file is sent.
 */
export interface UploadTracker {
  set: (name: string, busy: boolean) => void
  isBusy: () => boolean
  subscribe: (listener: () => void) => () => void
  settled: () => Promise<void>
}

export function createUploadTracker(): UploadTracker {
  const busy = new Set<string>()
  const listeners = new Set<() => void>()
  let waiters: Array<() => void> = []
  return {
    set(name, on) {
      if (on === busy.has(name)) return
      if (on) busy.add(name)
      else busy.delete(name)
      listeners.forEach((listener) => listener())
      if (busy.size === 0) {
        waiters.forEach((resolve) => resolve())
        waiters = []
      }
    },
    isBusy: () => busy.size > 0,
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    settled: () =>
      busy.size === 0 ? Promise.resolve() : new Promise<void>((resolve) => waiters.push(resolve)),
  }
}

const UploadTrackerContext = createContext<UploadTracker | null>(null)
export const UploadTrackerProvider = UploadTrackerContext.Provider

/** Tell the enclosing form while this field has a file on its way up. */
export function useReportUploading(name: string, busy: boolean): void {
  const tracker = useContext(UploadTrackerContext)
  useEffect(() => {
    if (!tracker || !busy) return
    tracker.set(name, true)
    return () => tracker.set(name, false)
  }, [tracker, name, busy])
}

const noSubscription = () => () => {}
const notBusy = () => false

/** True while any field of the enclosing form has a file on its way up. */
export function useFormUploading(): boolean {
  const tracker = useContext(UploadTrackerContext)
  return useSyncExternalStore(tracker?.subscribe ?? noSubscription, tracker?.isBusy ?? notBusy)
}

/** Flatten React Hook Form's nested error tree into a `{ path: message }` map. */
export function flattenErrors(errors: FieldErrors): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const walk = (node: unknown, prefix: string) => {
    if (!node || typeof node !== 'object') return
    const message = (node as { message?: unknown }).message
    if (typeof message === 'string') {
      out[prefix] = message
      return
    }
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      if (key === 'ref' || key === 'type') continue
      walk(child, prefix ? `${prefix}.${key}` : key)
    }
  }
  for (const [key, node] of Object.entries(errors)) walk(node, key)
  return out
}

/**
 * Build a `FormContext` from a React Hook Form instance. `values` is
 * supplied by the caller so it can pass a reactive snapshot (via
 * `useWatch`) where reactivity matters. `submit` wires `submitForm()` to
 * the form's real submit pipeline; when omitted (field-level callbacks
 * never submit) it is a no-op.
 */
export function makeFormContext(
  methods: UseFormReturn<Record<string, unknown>>,
  values: Record<string, unknown>,
  submit?: () => Promise<void>,
  isUploading = false,
): FormContext {
  return {
    values,
    errors: flattenErrors(methods.formState.errors),
    isValid: Object.keys(methods.formState.errors).length === 0,
    isSubmitting: methods.formState.isSubmitting,
    isUploading,
    setFieldValue: (field, value) =>
      methods.setValue(field, value as never, { shouldDirty: true, shouldValidate: false }),
    setFieldError: (field, message) =>
      message ? methods.setError(field, { type: 'manual', message }) : methods.clearErrors(field),
    setValues: (next) => methods.reset(next, { keepErrors: true }),
    submitForm: submit ?? (async () => {}),
    validateForm: async () => {
      await methods.trigger()
      return flattenErrors(methods.formState.errors)
    },
    resetForm: (next) => methods.reset(next?.values),
  }
}

/**
 * Reactive neutral form context for components rendered inside a form
 * (field callbacks, `visible` predicates, custom renderers). Re-renders
 * when any value changes so reads of `ctx.values` are always current.
 */
export function useFormContext(): FormContext {
  const methods = useReactHookFormContext<Record<string, unknown>>()
  const values = (useWatch({ control: methods.control }) as Record<string, unknown>) ?? {}
  const isUploading = useFormUploading()
  return makeFormContext(methods, values, undefined, isUploading)
}

export interface FieldStateResult {
  value: unknown
  error: string | undefined
  touched: boolean
  setValue: (value: unknown) => void
  clearError: () => void
  markTouched: () => void
}

/** Per-field state, mapping RHF's controller into a small neutral shape. */
export function useFieldState(name: string): FieldStateResult {
  const methods = useReactHookFormContext<Record<string, unknown>>()
  const { field, fieldState, formState } = useController({ name, control: methods.control })
  return {
    value: field.value,
    error: fieldState.error?.message,
    // Mirror the prior behaviour: a field counts as touched once the form
    // is submitted, so required errors surface even without a blur. An error
    // the caller sets (a server refusal) shows at once: a field picked by a
    // tap never blurs.
    touched:
      fieldState.isTouched || formState.isSubmitted || fieldState.error?.type === 'manual',
    setValue: (value) => field.onChange(value),
    clearError: () => methods.clearErrors(name),
    markTouched: () => methods.setValue(name, methods.getValues(name) as never, { shouldTouch: true }),
  }
}
